import React from 'react';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import CircularProgress from '@mui/joy/CircularProgress';
import Sheet from '@mui/joy/Sheet';
import Divider from '@mui/joy/Divider';
import { ChatCircle as ChatBubbleOutlineRounded, Ticket as ConfirmationNumberOutlined } from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import type { SupportTicket } from '../../types/support';
import { useTickets } from '../../hooks/useSupport';
import { StatusChip, PriorityChip } from './TicketStatusChip';

interface TicketListProps {
  onSelectTicket?: (ticket: SupportTicket) => void;
  selectedTicketId?: string | null;
  filters?: Record<string, string>;
  compact?: boolean;
}

export default function TicketList({ onSelectTicket, selectedTicketId, filters, compact = false }: TicketListProps) {
  const { data, isLoading } = useTickets(filters);
  const tickets = data?.results ?? [];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size="sm" />
      </Box>
    );
  }

  if (tickets.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
        <ConfirmationNumberOutlined size={40} color="var(--joy-palette-text-tertiary)" style={{ marginBottom: 8 }} />
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          No tickets yet
        </Typography>
      </Box>
    );
  }

  return (
    <Stack divider={<Divider />} sx={{ overflowY: 'auto' }}>
      {tickets.map(ticket => (
        <Sheet
          key={ticket.id}
          variant="plain"
          onClick={() => onSelectTicket?.(ticket)}
          sx={{
            p: compact ? 1.5 : 2,
            cursor: onSelectTicket ? 'pointer' : 'default',
            bgcolor: selectedTicketId === ticket.id ? 'primary.softBg' : 'transparent',
            '&:hover': onSelectTicket ? { bgcolor: 'background.level1' } : {},
            transition: 'background-color 0.15s',
          }}
        >
          <Stack spacing={0.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Typography level={compact ? 'body-sm' : 'title-sm'} sx={{ fontWeight: 600, flex: 1 }}>
                {ticket.subject}
              </Typography>
              <StatusChip status={ticket.status} />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                {ticket.ticket_number}
              </Typography>
              <PriorityChip priority={ticket.priority} />
              {ticket.organization_name && (
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                  {ticket.organization_name}
                </Typography>
              )}
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <ChatBubbleOutlineRounded size={12} color="var(--joy-palette-text-tertiary)" />
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  {ticket.message_count}
                </Typography>
              </Stack>
              <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
              </Typography>
            </Stack>
          </Stack>
        </Sheet>
      ))}
    </Stack>
  );
}
